import { motion } from 'framer-motion';

interface AnimatedLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}

export function AnimatedLogo({ 
  size = 'md',
  variant = 'default',
  className = ''
}: AnimatedLogoProps) {
  const sizeClasses = {
    sm: 'h-16',
    md: 'h-20',
    lg: 'h-28',
    xl: 'h-36'
  };

  // Color schemes for different variants (matching Logo.tsx)
  const colors = {
    default: {
      primary: '#2d5016',    // Forest green
      secondary: '#c41e3a',  // Deep red
      accent: '#f4a460',     // Gold
      text: '#2d3319'        // Dark foreground
    },
    light: {
      primary: '#5a7139',    // Lighter green
      secondary: '#e8624f',  // Lighter red
      accent: '#f4a460',     // Gold
      text: '#ffffff'        // White
    },
    dark: {
      primary: '#2d5016',    // Forest green
      secondary: '#c41e3a',  // Deep red
      accent: '#f4a460',     // Gold
      text: '#faf7f2'        // Cream
    }
  };

  const c = colors[variant];

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 400 120"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Better to Give than Receive - Give Better Gifts"
      >
        {/* Animated compass/star icon - matches Logo.tsx design */}
        <motion.g 
          transform="translate(35, 60)"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Outer petals - Forest Green (cardinal directions) */}
          <motion.path
            d="M 0,-30 L 3,-10 L 0,-8 L -3,-10 Z"
            fill={c.primary}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          />
          <motion.path
            d="M 0,30 L -3,10 L 0,8 L 3,10 Z"
            fill={c.primary}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          />
          <motion.path
            d="M -30,0 L -10,-3 L -8,0 L -10,3 Z"
            fill={c.primary}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          />
          <motion.path
            d="M 30,0 L 10,3 L 8,0 L 10,-3 Z"
            fill={c.primary}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          />
          
          {/* Diagonal petals - Deep Red */}
          <motion.path
            d="M -21,-21 L -7,-7 L -5,-9 L -9,-13 Z"
            fill={c.secondary}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
          />
          <motion.path
            d="M 21,21 L 7,7 L 9,5 L 13,9 Z"
            fill={c.secondary}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          />
          <motion.path
            d="M -21,21 L -7,7 L -9,5 L -13,9 Z"
            fill={c.secondary}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.3 }}
          />
          <motion.path
            d="M 21,-21 L 7,-7 L 5,-9 L 9,-13 Z"
            fill={c.secondary}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
          />
          
          {/* Center circles - Gold with animated appearance */}
          <motion.circle 
            cx="0" 
            cy="0" 
            r="8" 
            fill={c.accent}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.9, duration: 0.4, ease: "easeOut" }}
          />
          <motion.circle 
            cx="0" 
            cy="0" 
            r="5" 
            fill={c.primary} 
            opacity="0.8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.0, duration: 0.3 }}
          />
          <motion.circle 
            cx="0" 
            cy="0" 
            r="3" 
            fill={c.secondary} 
            opacity="0.6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.1, duration: 0.3 }}
          />
        </motion.g>

        {/* Ribbon/bow accent */}
        <motion.g 
          transform="translate(35, 60)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.4 }}
        >
          <path
            d="M -12,-2 Q -15,-5 -12,-8 L -8,-6 L -12,-2 Z"
            fill={c.secondary}
            opacity="0.7"
          />
          <path
            d="M 12,-2 Q 15,-5 12,-8 L 8,-6 L 12,-2 Z"
            fill={c.secondary}
            opacity="0.7"
          />
        </motion.g>

        {/* Text - fades in after icon */}
        <motion.g
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          {/* Main text: "BETTER TO GIVE THAN RECEIVE" - positions match Logo.tsx exactly */}
          <text
            x="79"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill={c.secondary}
          >
            BETTER
          </text>
          <text
            x="163"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="11"
            fontWeight="600"
            letterSpacing="0.5"
            fill={c.primary}
            opacity="0.6"
          >
            TO
          </text>
          <text
            x="186"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill={c.accent}
          >
            GIVE
          </text>
          <text
            x="238"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="11"
            fontWeight="600"
            letterSpacing="0.5"
            fill={c.primary}
            opacity="0.6"
          >
            THAN
          </text>
          <text
            x="278"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill={c.primary}
          >
            RECEIVE
          </text>

          {/* Tagline: "GIVE BETTER GIFTS" - positions match Logo.tsx exactly */}
          <text
            x="115"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill={c.accent}
          >
            GIVE
          </text>

          {/* Star between GIVE and BETTER */}
          <g transform="translate(168, 70)">
            <polygon
              points="0,-4 1.2,-1.2 4,-1.2 1.6,0.8 2.4,4 0,2 -2.4,4 -1.6,0.8 -4,-1.2 -1.2,-1.2"
              fill="white"
              stroke={c.primary}
              strokeWidth="1"
            />
          </g>

          <text
            x="178"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill={c.secondary}
          >
            BETTER
          </text>

          {/* Star between BETTER and GIFTS */}
          <g transform="translate(257, 70)">
            <polygon
              points="0,-4 1.2,-1.2 4,-1.2 1.6,0.8 2.4,4 0,2 -2.4,4 -1.6,0.8 -4,-1.2 -1.2,-1.2"
              fill="white"
              stroke={c.primary}
              strokeWidth="1"
            />
          </g>

          <text
            x="265"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill={c.primary}
          >
            GIFTS
          </text>
        </motion.g>
      </svg>
    </div>
  );
}

// Animated icon with gentle pulse - matches LogoIcon design from Logo.tsx
export function AnimatedLogoIcon({ 
  size = 'md',
  variant = 'default',
  className = '' 
}: { 
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16'
  };

  const colors = {
    default: {
      primary: '#2d5016',
      secondary: '#c41e3a',
      accent: '#f4a460'
    },
    light: {
      primary: '#5a7139',
      secondary: '#e8624f',
      accent: '#f4a460'
    },
    dark: {
      primary: '#2d5016',
      secondary: '#c41e3a',
      accent: '#f4a460'
    }
  };

  const c = colors[variant];

  return (
    <motion.div 
      className={`flex items-center justify-center ${className}`}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.2 }}
    >
      <svg
        viewBox="0 0 60 60"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="BtGtR"
      >
        {/* Outer petals - Forest Green (cardinal directions) */}
        <motion.path
          d="M 30,5 L 33,25 L 30,27 L 27,25 Z"
          fill={c.primary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        />
        <motion.path
          d="M 30,55 L 27,35 L 30,33 L 33,35 Z"
          fill={c.primary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        />
        <motion.path
          d="M 5,30 L 25,27 L 27,30 L 25,33 Z"
          fill={c.primary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        />
        <motion.path
          d="M 55,30 L 35,33 L 33,30 L 35,27 Z"
          fill={c.primary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        />
        
        {/* Diagonal petals - Deep Red */}
        <motion.path
          d="M 12,12 L 23,23 L 21,25 L 18,22 Z"
          fill={c.secondary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        />
        <motion.path
          d="M 48,48 L 37,37 L 39,35 L 42,38 Z"
          fill={c.secondary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        />
        <motion.path
          d="M 12,48 L 23,37 L 21,35 L 18,38 Z"
          fill={c.secondary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        />
        <motion.path
          d="M 48,12 L 37,23 L 35,21 L 38,18 Z"
          fill={c.secondary}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.3 }}
        />
        
        {/* Center circles - Gold with gentle pulse */}
        <motion.circle 
          cx="30" 
          cy="30" 
          r="10" 
          fill={c.accent}
          animate={{ 
            scale: [1, 1.05, 1],
            opacity: [1, 0.9, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.circle 
          cx="30" 
          cy="30" 
          r="7" 
          fill={c.primary} 
          opacity="0.8"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.9, duration: 0.3 }}
        />
        <motion.circle 
          cx="30" 
          cy="30" 
          r="4" 
          fill={c.secondary} 
          opacity="0.6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.0, duration: 0.3 }}
        />
      </svg>
    </motion.div>
  );
}

// Loading animation - compass/star rotates
export function LoadingLogo() {
  const colors = {
    primary: '#2d5016',
    secondary: '#c41e3a',
    accent: '#f4a460'
  };

  return (
    <div className="flex items-center justify-center">
      <svg
        viewBox="0 0 60 60"
        className="h-16 w-16"
        xmlns="http://www.w3.org/2000/svg"
      >
        <motion.g 
          style={{ transformOrigin: '30px 30px' }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          {/* Outer petals - Forest Green (cardinal directions) */}
          <path
            d="M 30,5 L 33,25 L 30,27 L 27,25 Z"
            fill={colors.primary}
          />
          <path
            d="M 30,55 L 27,35 L 30,33 L 33,35 Z"
            fill={colors.primary}
          />
          <path
            d="M 5,30 L 25,27 L 27,30 L 25,33 Z"
            fill={colors.primary}
          />
          <path
            d="M 55,30 L 35,33 L 33,30 L 35,27 Z"
            fill={colors.primary}
          />
          
          {/* Diagonal petals - Deep Red */}
          <path
            d="M 12,12 L 23,23 L 21,25 L 18,22 Z"
            fill={colors.secondary}
          />
          <path
            d="M 48,48 L 37,37 L 39,35 L 42,38 Z"
            fill={colors.secondary}
          />
          <path
            d="M 12,48 L 23,37 L 21,35 L 18,38 Z"
            fill={colors.secondary}
          />
          <path
            d="M 48,12 L 37,23 L 35,21 L 38,18 Z"
            fill={colors.secondary}
          />
        </motion.g>
        
        {/* Center circles - Gold (static, doesn't rotate) */}
        <motion.circle 
          cx="30" 
          cy="30" 
          r="10" 
          fill={colors.accent}
          animate={{ 
            scale: [1, 1.15, 1],
            opacity: [1, 0.8, 1]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <circle cx="30" cy="30" r="7" fill={colors.primary} opacity="0.8" />
        <circle cx="30" cy="30" r="4" fill={colors.secondary} opacity="0.6" />
      </svg>
    </div>
  );
}
